import { useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphScopes, isAzureAdConfigured } from '../auth/msalConfig';

/**
 * Custom hook for Microsoft Graph API authentication
 * Handles token acquisition, login, and logout
 */
export function useGraphAuth() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  // Check if Azure AD is configured
  const isConfigured = isAzureAdConfigured();

  // MSAL hooks - only use if configured
  let instance = null;
  let accounts = [];
  let isAuthenticated = false;

  try {
    const msalHooks = useMsal();
    instance = msalHooks.instance;
    accounts = msalHooks.accounts;
    isAuthenticated = useIsAuthenticated();
  } catch (e) {
    // MSAL not available (not configured)
  }

  /**
   * Get access token for Microsoft Graph API
   * Will trigger login popup if not authenticated
   */
  const getAccessToken = useCallback(async () => {
    if (!isConfigured || !instance) {
      throw new Error('Azure AD authentication not configured');
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      if (accounts.length === 0) {
        // No account - need to login
        const response = await instance.loginPopup(graphScopes);
        instance.setActiveAccount(response.account);
        return response.accessToken;
      }

      // Try silent token acquisition first
      const request = {
        ...graphScopes,
        account: accounts[0],
      };

      try {
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (silentError) {
        // Silent acquisition failed - try popup
        if (silentError instanceof InteractionRequiredAuthError) {
          const response = await instance.acquireTokenPopup(request);
          return response.accessToken;
        }
        throw silentError;
      }
    } catch (err) {
      // Provide helpful message for common Azure AD configuration errors
      let errorMessage = err.message || 'Authentication failed';

      // AADSTS9002326: Cross-origin token redemption error
      if (err.message?.includes('AADSTS9002326') || err.message?.includes('Cross-origin token redemption')) {
        errorMessage = 'Azure AD configuration error: The redirect URI must be configured as "Single-page application (SPA)" type in Azure Portal, not "Web" type. Go to Azure Portal > App registrations > your app > Authentication > Platform configurations.';
      }

      setError(errorMessage);

      // Create enhanced error for upstream handling
      const enhancedError = new Error(errorMessage);
      enhancedError.code = err.message?.includes('user_cancelled') ? 'user_cancelled' : err.code;
      throw enhancedError;
    } finally {
      setIsAuthenticating(false);
    }
  }, [instance, accounts, isConfigured]);

  /**
   * Trigger login popup
   */
  const login = useCallback(async () => {
    if (!isConfigured || !instance) {
      throw new Error('Azure AD authentication not configured');
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      const response = await instance.loginPopup(graphScopes);
      instance.setActiveAccount(response.account);
      return response;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [instance, isConfigured]);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    if (!instance) return;

    try {
      await instance.logoutPopup();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  /**
   * Get current user info
   */
  const currentUser = accounts.length > 0 ? {
    name: accounts[0].name,
    email: accounts[0].username,
  } : null;

  return {
    isConfigured,
    isAuthenticated,
    isAuthenticating,
    error,
    currentUser,
    getAccessToken,
    login,
    logout,
  };
}

export default useGraphAuth;
